import React, { useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { useToggle } from 'react-use';
import { ButtonComponent } from '@zolteam/axenergie-ui-library';
// Constants
import titleMetaDescription from '../../constants/TitleMetaDescription';
import strings from '../../constants/Strings';
import colors from '../../constants/Colors';
// Components
import Menu from '../../components/Molecules/Menu';
import HeaderWithSlashContainer from '../../components/Atoms/HeaderWithSlashContainer';
import Footer from '../../components/Molecules/Footer';
import ReassuranceBanner from '../../components/Molecules/ReassuranceBanner';
import JobOfferApplyModal from '../../components/Organisms/JobOfferApplyModal';
import JobOfferDomainItem from '../../components/Atoms/JobOfferDomainItem';
// Services and hooks
import menuAndFooterService from '../../services/contentful/menuAndFooterService';
import jobOfferDomainPageService from '../../services/contentful/jobOfferDomainPageService';
import jobOfferDomainsService from '../../services/api/jobOfferDomainsService';
import useIsMobile from '../../hooks/useIsMobile';
import useLockBodyScrollNotIOS from '../../hooks/useLockBodyScrollNotIOS';
// Utils
import utils from '../../utils/formatCMS';
// Images
import logo from '../../public/Images/logo-white.webp';

const JobOfferList = (props) => {
  const router = useRouter();
  const [isMobile] = useIsMobile();
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [locked, toggleLocked] = useToggle(false);
  useLockBodyScrollNotIOS(locked);

  const toggleApplyModal = (value) => {
    setIsApplyModalOpen(value);
    toggleLocked();
  };

  // Queries
  const getJobOfferDomains = useQuery(['getJobOfferDomains'], () => jobOfferDomainsService.getJobOfferDomains());

  const titleTag = titleMetaDescription.jobOfferPage.title;
  const descriptionTag = titleMetaDescription.jobOfferPage.description;

  return (
    <div>
      <Head>
        <title>{titleTag}</title>
        <meta name="description" content={descriptionTag} />
        <meta property="og:title" content={titleTag} />
        <meta property="og:description" content={descriptionTag} />
        <meta
          property="og:image"
          content="/Images/job-offer-illustration.webp"
        />
        <meta
          property="og:image:secure_url"
          content="/Images/job-offer-illustration.webp"
        />
      </Head>
      {/* header */}
      <div className="full-width d-flex f-row relative">
        {/* Menu */}
        <div className="absolute absolute-right-top-corner index-2">
          <Menu items={utils.formatCMSDatas('menu', props)} fontColor="grey-800-text" />
        </div>
        <HeaderWithSlashContainer
          imageUrl={isMobile ? null : '/Images/job-offer-illustration.webp'}
          backgroundColor={colors.grey800}
        >
          <div style={{ minHeight: isMobile ? 0 : 600 }}>
            {isMobile ? null : (
              <button type="button" onClick={() => router.push('/')}>
                <Image alt="logo" src={logo} width={206} height={206} />
              </button>
            )}
            <div className={`${isMobile ? 'mv-2 ph-3' : 'mv-6 pl-5'}`}>
              <h1 className="white-text medium-weight">{strings.jobOfferPageTitle}</h1>
              <p className="grey-400-text normal-weight">{strings.jobOfferPageSubtitle}</p>
            </div>
          </div>
        </HeaderWithSlashContainer>
      </div>
      {/* spontaneous application block */}
      <div className="d-flex f-row ph-3 pv-5 justify-center">
        <div
          className="d-flex f-column"
          style={{ width: isMobile ? '100%' : '80%' }}
        >
          <h2 className="medium-weight grey-800-text text-align-center">{strings.spontaneousApplicationTitle}</h2>
          {getJobOfferDomains?.data?.data?.jobOfferDomains?.map((domain, index) => (
            props.domains?.[domain.code]?.illustration?.fields?.file?.url
            || props.domains?.[domain.code]?.title
            || props.domains?.[domain.code]?.slug
              ? (
                <JobOfferDomainItem
                  key={domain.code}
                  layout={index % 2 === 0 ? 'default' : 'inverted'}
                  title={props.domains?.[domain.code]?.title}
                  illustration={`https:${props.domains?.[domain.code]?.illustration?.fields?.file?.url}`}
                  introduction={props.domains?.[domain.code]?.introduction}
                  url={`/offres-d-emploi/${props.domains?.[domain.code]?.slug}`}
                />
              ) : null))}
          <div className="d-flex justify-center mv-5 pt-5">
            <ButtonComponent onClick={() => toggleApplyModal(true)} theme="outline">
              <div className="mv-2">
                <span>{strings.sendYourSpontaneousApplication}</span>
              </div>
            </ButtonComponent>
          </div>
          {isApplyModalOpen ? (
            <JobOfferApplyModal
              closeModal={() => toggleApplyModal(false)}
            />
          ) : null}
        </div>
      </div>
      {/* Reassurance banner */}
      <ReassuranceBanner items={utils.formatCMSReassurance(props)} />
      {/* Footer */}
      <Footer
        networkBtn={props.footerRseauLienBouton}
        clientItems={utils.formatCMSDatas('footerClient', props)}
        networkItems={utils.formatCMSDatas('footerRseau', props)}
        facebook={props.footerFacebook}
        instagram={props.footerInstagram}
        twitter={props.footerTwitter}
        mentions={props.mentions}
        legalItems={utils.formatCMSDatas('sousFooter', props)}
        isMobile={isMobile}
      />
    </div>
  );
};

export default JobOfferList;

export async function getServerSideProps() {
  let menuAndFooter;
  let jobOfferDomainAdministrativeAgent;
  let jobOfferDomainCoolingTechnician;
  let jobOfferDomainHeatingTechnician;
  let jobOfferDomainMaintenanceManager;
  let jobOfferDomainPlumber;

  try {
    menuAndFooter = await menuAndFooterService.menuAndFooter();
  } catch (err) {
    menuAndFooter = {};
    console.error(err);
  }
  try {
    jobOfferDomainAdministrativeAgent = await jobOfferDomainPageService.administrativeAgent();
  } catch (err) {
    jobOfferDomainAdministrativeAgent = {};
    console.error(err);
  }
  try {
    jobOfferDomainCoolingTechnician = await jobOfferDomainPageService.coolingTechnician();
  } catch (err) {
    jobOfferDomainCoolingTechnician = {};
    console.error(err);
  }
  try {
    jobOfferDomainHeatingTechnician = await jobOfferDomainPageService.heatingTechnician();
  } catch (err) {
    jobOfferDomainHeatingTechnician = {};
    console.error(err);
  }
  try {
    jobOfferDomainMaintenanceManager = await jobOfferDomainPageService.maintenanceManager();
  } catch (err) {
    jobOfferDomainMaintenanceManager = {};
    console.error(err);
  }
  try {
    jobOfferDomainPlumber = await jobOfferDomainPageService.plumber();
  } catch (err) {
    jobOfferDomainPlumber = {};
    console.error(err);
  }
  return {
    props: {
      ...menuAndFooter?.fields,
      domains: {
        administrative_agent: jobOfferDomainAdministrativeAgent?.fields || {},
        cooling_technician: jobOfferDomainCoolingTechnician?.fields || {},
        heating_technician: jobOfferDomainHeatingTechnician?.fields || {},
        maintenance_manager: jobOfferDomainMaintenanceManager?.fields || {},
        plumber: jobOfferDomainPlumber?.fields || {},
      },
    },
  };
}
